/**
 * useSadaRayonAlerts — SADARayon push notifications + in-app sound/vibration.
 *
 * Multi-device support: each browser/device gets its own subscription
 * keyed by `PushSubscription.endpoint`. Re-running `subscribe()` on the same
 * device is idempotent (the backend upserts on endpoint).
 *
 * Returns:
 *   { isSupported, isSubscribed, loading, permission,
 *     subscribe, unsubscribe, sendTest, refresh }
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

const SW_PATH = '/service-worker.js';

export function useSadaRayonAlerts() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [loading, setLoading] = useState(false);

  const supported = useCallback(() => {
    return typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
  }, []);

  // Détecte iOS Safari en navigateur classique (pas encore installé en PWA).
  // iOS impose l'installation sur l'écran d'accueil avant d'autoriser le push.
  const isIosNonStandalone = useCallback(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (!isIos) return false;
    // Standalone PWA mode ?
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    return !standalone;
  }, []);

  const refresh = useCallback(async () => {
    if (!supported()) { setIsSupported(false); return; }
    setIsSupported(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH)
        || await navigator.serviceWorker.ready;
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setIsSubscribed(!!sub);
    } catch (e) {
      setIsSubscribed(false);
    }
    setPermission(Notification.permission);
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async () => {
    // iOS Safari ne supporte le Web Push QUE pour les PWA installées sur l'écran d'accueil.
    // Intercepter AVANT la demande de permission qui sortirait un message d'erreur générique.
    if (isIosNonStandalone()) {
      toast.error(
        'Sur iPhone : ouvrez d\'abord Safari → Partager → "Sur l\'écran d\'accueil" pour installer l\'app, puis réactivez les alertes depuis cette icône.',
        { duration: 12000 },
      );
      return false;
    }
    if (!supported()) {
      toast.error('Notifications push non supportées par ce navigateur.');
      return false;
    }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error(
          'Permission refusée. Sur iPhone, l\'app doit être installée sur l\'écran d\'accueil (Safari → Partager → "Sur l\'écran d\'accueil"). Sur Android/desktop, autorisez dans les réglages du site.',
          { duration: 10000 },
        );
        return false;
      }
      // Register/ensure service worker
      let reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      if (!reg) {
        reg = await navigator.serviceWorker.register(SW_PATH);
      }
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidRes = await axios.get(`${API}/inventory/push/vapid-public-key`, { headers: auth() });
      const vapidKey = vapidRes.data?.publicKey;
      if (!vapidKey) throw new Error('VAPID key indisponible.');

      // Reuse existing or subscribe new
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      const payload = {
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
          auth: arrayBufferToBase64(sub.getKey('auth')),
        },
        device_label: navigator.userAgent.slice(0, 100),
      };
      await axios.post(`${API}/inventory/push/subscribe`, payload, { headers: auth() });
      setIsSubscribed(true);
      toast.success('Alertes SADARayon activées sur cet appareil.');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Échec de l'abonnement.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported, isIosNonStandalone]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const endpoint = sub.endpoint;
        try {
          await axios.delete(`${API}/inventory/push/subscribe`, {
            headers: auth(),
            data: { endpoint },
          });
        } catch { /* ignore — best-effort */ }
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
      toast.success('Alertes SADARayon désactivées sur cet appareil.');
      return true;
    } catch (e) {
      toast.error(e.message || 'Échec de la désinscription.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/inventory/push/test`, {}, { headers: auth() });
      toast.success(`Notification test envoyée à ${res.data?.sent || 0} appareil(s).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi de test.");
    }
  }, []);

  const iosNeedsInstall = isIosNonStandalone();

  // Diagnostic technique — utile pour le support quand "ça ne marche pas".
  const diagnostic = {
    permission,
    isSupported,
    isSubscribed,
    iosNeedsInstall,
    hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    hasNotification: typeof window !== 'undefined' && 'Notification' in window,
    standalone: typeof window !== 'undefined' && (
      window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator?.standalone === true
    ),
    ua: typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 200) : '',
  };

  return {
    isSupported,
    isSubscribed,
    loading,
    permission,
    iosNeedsInstall,
    diagnostic,
    subscribe,
    unsubscribe,
    sendTest,
    refresh,
  };
}
